import { defineStore } from 'pinia'
import type { LlanaListOptions, LlanaListResponse } from '~/types/llana'
import type { Profile, UserSubscription, UserReport, Social } from '~/types/profile'

type T = Profile
const defaultRelations = ['users_subscriptions', 'users_socials']

export function useProfileSelfStore() {
	const store = 'profile_Store'

	return defineStore(store, {
		state: () => <T>{},
		actions: {
			async get(): Promise<T> {
				try {
					const { $llanaGetProfile } = useNuxtApp()
					const profile = ref<Profile>(
						await $llanaGetProfile<Profile>({
							relations: defaultRelations,
						}),
					)
					this.$state = profile.value
					return profile.value
				} catch (error) {
					console.error('Error getting profile:', error)
					return {} as T
				}
			},
			async isActive(): Promise<void> {
				const active_at = new Date()
				const { $llana } = useNuxtApp()
				await $llana({
					type: 'UPDATE',
					table: 'users',
					id: this.$state.id,
					data: { active_at: active_at.toISOString() },
				})
				this.$state.active_at = active_at
			},
			async subscribe(sub: Partial<UserSubscription>): Promise<T> {
				const { $llana } = useNuxtApp()
				const result = (await $llana<UserSubscription>({
					type: 'CREATE',
					table: 'users_subscriptions',
					data: sub,
				})) as UserSubscription
				this.$state.users_subscriptions.push(result as UserSubscription)
				return this.$state
			},
			async unsubscribe(id: number): Promise<T> {
				const { $llana } = useNuxtApp()
				await $llana<UserSubscription>({
					type: 'DELETE',
					table: 'users_subscriptions',
					id,
					hard: true,
				})
				const index = this.$state.users_subscriptions.findIndex(s => s.id === id)
				if (index > -1) {
					this.$state.users_subscriptions.splice(index, 1)
				}
				return this.$state
			},
			async report(data: Partial<UserReport>): Promise<UserReport> {
				const { $llana } = useNuxtApp()
				const result = await $llana<UserSubscription>({
					type: 'CREATE',
					table: 'users_reports',
					data,
				})
				return result as UserReport
			},
			async addSocial(data: Partial<Social>): Promise<Social> {
				const { $llana } = useNuxtApp()
				const result = await $llana<UserSubscription>({
					type: 'CREATE',
					table: 'users_socials',
					data,
				})
				this.$state.users_socials.push(result as Social)
				return result as Social
			},
			async deleteSocial(social_id: number): Promise<void> {
				const { $llana } = useNuxtApp()
				await $llana<UserSubscription>({
					type: 'DELETE',
					table: 'users_socials',
					id: social_id,
				})
				const index = this.$state.users_socials.findIndex(s => s.id === social_id)
				if (index > -1) {
					this.$state.users_socials.splice(index, 1)
				}
			},
			async setUsername(username: string): Promise<T> {
				const { $llana } = useNuxtApp()
				const profile = await $llana<Profile>({
					type: 'UPDATE',
					table: 'users',
					id: this.$state.id,
					data: { username },
				})

				this.$state = profile as T
				return profile as T
			},
			async setBio(bio: string): Promise<T> {
				const { $llana } = useNuxtApp()
				const profile = await $llana<Profile>({
					type: 'UPDATE',
					table: 'users',
					id: this.$state.id,
					data: { bio },
				})

				this.$state = profile as T
				return profile as T
			},
			async searchSubscriptions(options?: LlanaListOptions): Promise<LlanaListResponse<UserSubscription>> {
				const { $llana } = useNuxtApp()
				return (await $llana<UserSubscription>({
					type: 'LIST',
					table: 'users_subscriptions',
					sort: options?.sort || 'created_at.desc',
					limit: options?.limit || 20,
					offset: options?.offset || 0,
					page: options?.page,
					where: [
						{
							field: 'user_id',
							value: this.$state.id,
							operator: 'equals',
						},
					],
				})) as LlanaListResponse<UserSubscription>
			},
		},
	})
}
